import React, { useEffect, useState, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import _ from "lodash";

import {
  Drawer,
  Input,
  Flex,
  Modal,
  Space,
  Switch,
  Table,
  Button,
} from "antd";
import {
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  EditFilled,
  PlusOutlined,
  DownOutlined,
  RedoOutlined,
  CopyOutlined,
  ClearOutlined,

  CloseOutlined,
} from "@ant-design/icons";

import { v4 as uuidv4 } from "uuid";
import { jsonrepair } from "jsonrepair";
import { flushSync } from "react-dom";

import { NetworkScenariosContext } from "../index";
import { ActionButton } from "../../../components/elements/Button";
import { DropdownMenu } from "../../../components/elements/DropdownMenu";
import { NetworkSortableList } from "../NetworkSortableList";
import {
  defaultParameterNotation,
  transformDataObject,
  NestedObjectTable,
} from "../../../components/elements/JsonObjTable";

import { defaultMenuProps } from "../defaultEventTemplates";
import { validateDomainTLD, getTLDErrorMessage } from "../ScenariosMenu/utils";
import "./styles.scss";

const { TextArea } = Input;

const generateUniqueId = () => uuidv4();

const templateElemsList = (items) => {
  const normalizedItems = Array.isArray(items) ? items : [];

  switch (normalizedItems.length) {
    case 0:
      return [
        defaultParameterNotation({
          key: "",
          type: "string",
          required: true,
          exatclyMatch: false,
          value: "",
        }),
      ];
    default:
      return normalizedItems;
  }
};

const scenarioStartPageMatchTypes = [
  { label: "exact matches", key: "exact_matches" },
  { label: "matches regex", key: "matches_regex" },
  { label: "contains", key: "contains" },
];

const scenarioStartPageTypes = [
  { label: "hostname", key: "hostname" },
  { label: "page url", key: "page_url" },
  { label: "page path", key: "page_path" },
];

const defaultScenarioObject = () => ({
  scenarioID: generateUniqueId(),
  scenarioTitle: "",
  active: false,
  urlStartingPoint: "",
  urlStartingPointMatchType: "",
  networkEndpointPattern: "",
  cookiesAcceptance: {
    active: false,
    cookiesPassed: false,
    steps: [],
  },
  loginInfo: {
    required: false,
    isLoggedIn: false,
    steps: [],
  },
  steps: [],
});

const ConfirmationModalWindow = (props) => {
  const {
    open = false,
    onOk = () => {},
    onCancel = () => {},
    text = "",
  } = props;

  return (
    <Modal
      centered
      title={"Confirm action"}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      footer={(_, { OkBtn, CancelBtn }) => (
        <Flex gap={5}>
          <CancelBtn />
          <OkBtn />
        </Flex>
      )}
    >
      {text}
    </Modal>
  );
};

const NetworkScenariosMenu = () => {
  const {
    companyId = "",
    configParams = {},
    setConfigParams = () => {},
    networkScenariosDescLink,
    scenariosList = [],
    drawerGlobalState = false,
    setDrawerGlobalState = () => {},
    saveFunction = () => {},
    defaultEventTemplates = [],
    simplifyDLGEventJson = () => {},
    transformDataObject = () => {},
    error = () => {},
  } = useContext(NetworkScenariosContext);

  const defaultPlacement = "right";
  const secondDrawerTitle = "Scenario Steps List";
  const thirdDrawerTitle = "Network Event Template";
  const cookiesDrawerTitle = "Cookies Scenario Steps List";
  const firstDrawerWidth = "75%";
  const secondDrawerWidth = "75%";
  const thirdDrawerWidth = "75%";

  const defaultMatchType = scenarioStartPageMatchTypes[0]?.key;
  const defaultPageType = scenarioStartPageTypes[0]?.key;

  const [jsonBtnText, setJsonBtnText] = useState("add json");
  const [jsonBtnIcon, setJsonBtnIcon] = useState(<PlusOutlined />);

  const [templateModalContentCode, setTemplateModalContentCode] =
    useState("main");
  const [thirdDrawerClassName, setThirdDrawerClassName] = useState("");

  const [secondDrawerVisible, setSecondDrawerVisible] = useState(false);
  const [thirdDrawerVisible, setThirdDrawerVisible] = useState(false);
  const [cookiesDrawerVisible, setCookiesDrawerVisible] = useState(false);
  const [loginDrawerVisible, setLoginDrawerVisible] = useState(false);

  const [scenarioSaveStatus, setScenarioSaveStatus] = useState(false);
  const [isScenarioSaving, setIsScenarioSaving] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const initialScenarioRef = useRef(null);

  const [templateSaveStatus, setTemplateSaveStatus] = useState(false);
  const [showTemplateUnsavedModal, setShowTemplateUnsavedModal] =
    useState(false);
  const initialTemplateRef = useRef(null);

  const showSecondDrawer = () => setSecondDrawerVisible(true);
  const closeSecondDrawer = () => setSecondDrawerVisible(false);

  const showCookiesDrawer = () => setCookiesDrawerVisible(true);
  const closeCookiesDrawer = () => setCookiesDrawerVisible(false);

  const showLoginDrawer = () => setLoginDrawerVisible(true);
  const closeLoginDrawer = () => setLoginDrawerVisible(false);

  const showThirdDrawer = (item, stepId) => {
    const parametersList = templateElemsList(item);
    initialTemplateRef.current = JSON.parse(JSON.stringify(parametersList));
    setSelectedTemplate(parametersList);
    setSelectedTemplateStr(JSON.stringify(parametersList) || "");
    setSelectedStepId(stepId);
    setTemplateModalContentCode("main");
    setTemplateSaveStatus(false);
    setThirdDrawerVisible(true);
  };
  const closeThirdDrawer = () => {
    setThirdDrawerVisible(false);
    setTemplateModalContentCode("main");
  };

  useEffect(() => {
    const widthClass = templateModalContentCode === "edit" ? "modal-wide" : "";
    setThirdDrawerClassName(widthClass);
  }, [templateModalContentCode, thirdDrawerVisible]);

  const [curScenariosList, setCurScenariosList] = useState(null);
  const [curScenariosEvents, setCurScenariosEvents] = useState({});

  useEffect(() => {
    setCurScenariosList(scenariosList);
    const scenariosDropdownList = scenariosList.map((elem, idx) => ({
      key: elem?.scenarioID || idx,
      label: elem?.scenarioTitle || `Untitled Scenario ${idx + 1}`,
    }));

    setStepsScenarioOptions(
      getScenariosListProps({
        listUpdateFunction: setScenarioSteps,
        scenariosDropdownList,
        stepsType: "steps",
        modalCancelFunction: handleStepsListModalCancel,
        modalStateChangeFunction: setStepsListChangeModalState,
      }),
    );

    setCookiesStepsScenarioOptions(
      getScenariosListProps({
        listUpdateFunction: setScenarioCookiesSteps,
        scenariosDropdownList,
        stepsType: "cookies",
        modalCancelFunction: handleCookiesStepsListModalCancel,
        modalStateChangeFunction: setCookiesStepsListChangeModalState,
      }),
    );

    setLoginStepsScenarioOptions(
      getScenariosListProps({
        listUpdateFunction: setScenarioLoginSteps,
        scenariosDropdownList,
        stepsType: "login",
        modalCancelFunction: handleLoginStepsListModalCancel,
        modalStateChangeFunction: setLoginStepsListChangeModalState,
      }),
    );

    const scenariosEvents = {};
    scenariosList.forEach((elem) => {
      const events = elem?.steps?.filter((step) =>
        ["collect_network_event"].includes(step?.type),
      );
      scenariosEvents[elem.scenarioID] =
        events?.map((event) => ({
          stepNum: event?.step,
          eventName:
            event?.networkFilters
              ?.map((f) => `${f.key}=${f.value}`)
              .join(", ") || "",
        })) || [];
    });
    setCurScenariosEvents(scenariosEvents);
  }, [scenariosList]);

  const [pauseModalVisible, setPauseModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [targetScenarioId, setTargetScenarioId] = useState(null);

  const confirmDeleteScenario = (id) => {
    setTargetScenarioId(id);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirmed = () => {
    if (!targetScenarioId) return;
    flushSync(() => {
      setDeleteModalVisible(false);
      setTargetScenarioId(null);
      scenarioRemove(targetScenarioId);
    });
  };

  const scenarioRemove = (id) => {
    const newScenariosList = curScenariosList.filter(
      (s) => s.scenarioID !== id,
    );
    setCurScenariosList(newScenariosList);
    setScenarioRemoveConfirmation(false);
    closeSecondDrawer();
    const updated = { ...configParams, network_scenarios: newScenariosList };
    setConfigParams(updated);
    setIsConfigParamsUpdatedBeforeSave(true);
  };

  const confirmPauseScenario = (id) => {
    setTargetScenarioId(id);
    setPauseModalVisible(true);
  };

  const handlePauseConfirmed = () => {
    if (!targetScenarioId) return;
    flushSync(() => {
      setPauseModalVisible(false);
      setTargetScenarioId(null);
      scenarioPause(targetScenarioId);
    });
  };

  const scenarioPause = (id) => {
    const newList = curScenariosList.map((s) =>
      s.scenarioID === id ? { ...s, active: false } : s,
    );
    setCurScenariosList(newList);
    const updated = { ...configParams, network_scenarios: newList };
    setConfigParams(updated);
    setIsConfigParamsUpdatedBeforeSave(true);
    closeSecondDrawer();
  };

  const activateScenario = (id) => {
    const newList = curScenariosList.map((s) =>
      s.scenarioID === id ? { ...s, active: true } : s,
    );
    setCurScenariosList(newList);
    const updated = { ...configParams, network_scenarios: newList };
    setConfigParams(updated);
    setIsConfigParamsUpdatedBeforeSave(true);
    closeSecondDrawer();
  };

  const headerStyle = {
    background: "#fff",
    backgroundImage: "none",
    boxShadow: "none",
    filter: "none",
  };

  const scenarioColumns = [
    {
      title: "Scenario name",
      dataIndex: "scenarioTitle",
      key: "scenarioTitle",
      align: "left",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Monitored events",
      dataIndex: "left",
      key: "events",
      align: "left",
      render: (_, record) => (
        <span style={{ fontStyle: "italic" }}>
          {curScenariosEvents[record?.scenarioID]
            ?.map((event) => event?.eventName)
            .filter(Boolean)
            .join(", ")}
        </span>
      ),
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "left",
      align: "middle",
      render: (active) => (active ? "active" : "paused"),
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Space size={14}>
          <DeleteOutlined
            style={{ color: "#000", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              confirmDeleteScenario(record?.scenarioID);
            }}
          />
          {record.active ? (
            <PauseCircleOutlined
              style={{ color: "#000", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                confirmPauseScenario(record.scenarioID);
              }}
            />
          ) : (
            <PlayCircleOutlined
              style={{ color: "#000", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                activateScenario(record.scenarioID);
              }}
            />
          )}
          <EditOutlined
            style={{ color: "#000", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              handleEditScenarioClick(record?.scenarioID);
            }}
          />
        </Space>
      ),
      onHeaderCell: () => ({ style: headerStyle }),
    },
  ];

  const [selectedScenario, setSelectedScenario] = useState(null);
  const [scenarioTitle, setScenarioTitle] = useState("");
  const [scenarioStatus, setScenarioStatus] = useState(false);
  const [scenarioStartPage, setScenarioStartPage] = useState(false);
  const [scenarioStartPageMatchType, setScenarioStartPageMatchType] =
    useState(null);
  const [scenarioStartPageType, setScenarioStartPageType] = useState(null);
  const [networkEndpointPattern, setNetworkEndpointPattern] = useState("");
  const [scenarioSteps, setScenarioSteps] = useState([]);

  const [urlTldValid, setUrlTldValid] = useState(true);
  const [urlTldError, setUrlTldError] = useState("");

  const [scenarioCookies, setScenarioCookies] = useState(null);
  const [scenarioCookiesStatus, setScenarioCookiesStatus] = useState(false);
  const [scenarioCookiesSteps, setScenarioCookiesSteps] = useState(false);

  const [scenarioLogin, setScenarioLogin] = useState(null);
  const [scenarioLoginStatus, setScenarioLoginStatus] = useState(false);
  const [scenarioLoginSteps, setScenarioLoginSteps] = useState(false);

  const [scenarioEvents, setScenarioEvents] = useState([]);

  useEffect(() => {
    setScenarioTitle(selectedScenario?.scenarioTitle || "");
    setScenarioStatus(!!selectedScenario?.active);
    setScenarioStartPage(selectedScenario?.urlStartingPoint || "");
    setScenarioStartPageMatchType(
      selectedScenario?.urlStartingPointMatchType || defaultMatchType,
    );
    setScenarioStartPageType(
      selectedScenario?.urlStartingPointPageType || defaultPageType,
    );
    setNetworkEndpointPattern(selectedScenario?.networkEndpointPattern || "");
    setScenarioSteps(selectedScenario?.steps || []);
    setScenarioCookies(selectedScenario?.cookiesAcceptance || {});
    setScenarioLogin(selectedScenario?.loginInfo || {});
    setScenarioEvents(curScenariosEvents[selectedScenario?.scenarioID]);

    const startPage = selectedScenario?.urlStartingPoint || "";
    if (startPage && startPage.trim() !== "") {
      const isValid = validateDomainTLD(startPage);
      setUrlTldValid(isValid);
      setUrlTldError(isValid ? "" : getTLDErrorMessage(startPage));
    } else {
      setUrlTldValid(true);
      setUrlTldError("");
    }
  }, [selectedScenario]);

  useEffect(() => {
    setScenarioCookiesStatus(!!scenarioCookies?.active);
    setScenarioCookiesSteps(scenarioCookies?.steps || []);
  }, [scenarioCookies]);

  useEffect(() => {
    setScenarioLoginStatus(!!scenarioLogin?.required);
    setScenarioLoginSteps(scenarioLogin?.steps || []);
  }, [scenarioLogin]);

  useEffect(() => {
    if (selectedScenario && secondDrawerVisible) {
      initialScenarioRef.current = {
        title: selectedScenario?.scenarioTitle || "",
        startPage: selectedScenario?.urlStartingPoint || "",
        matchType:
          selectedScenario?.urlStartingPointMatchType || defaultMatchType,
        pageType: selectedScenario?.urlStartingPointPageType || defaultPageType,
        networkEndpointPattern:
          selectedScenario?.networkEndpointPattern || "",
        steps: selectedScenario?.steps || [],
      };
      setScenarioSaveStatus(false);
    }
  }, [selectedScenario?.scenarioID, secondDrawerVisible]);

  useEffect(() => {
    if (!initialScenarioRef.current || !secondDrawerVisible) return;
    const currentState = {
      title: scenarioTitle,
      startPage: scenarioStartPage,
      matchType: scenarioStartPageMatchType,
      pageType: scenarioStartPageType,
      networkEndpointPattern: networkEndpointPattern,
      steps: scenarioSteps,
    };
    const hasChanges = !_.isEqual(initialScenarioRef.current, currentState);
    setScenarioSaveStatus(hasChanges);
  }, [
    scenarioTitle,
    scenarioStartPage,
    scenarioStartPageMatchType,
    scenarioStartPageType,
    networkEndpointPattern,
    scenarioSteps,
    secondDrawerVisible,
  ]);

  const handleStartingPointUrlChange = (e) => {
    const value = e.target.value;
    setScenarioStartPage(value);
    if (value && value.trim() !== "") {
      const isValid = validateDomainTLD(value);
      setUrlTldValid(isValid);
      setUrlTldError(isValid ? "" : getTLDErrorMessage(value));
    } else {
      setUrlTldValid(true);
      setUrlTldError("");
    }
  };

  const menuMatchTypes = {
    items: scenarioStartPageMatchTypes.map(({ key, label }) => ({
      key,
      label,
    })),
    onClick: ({ key }) => setScenarioStartPageMatchType(key),
  };

  const menuPageTypes = {
    items: scenarioStartPageTypes.map(({ key, label }) => ({ key, label })),
    onClick: ({ key }) => setScenarioStartPageType(key),
  };

  const handleEditScenarioClick = (id) => {
    const scenarioObj = curScenariosList.find((elem) => elem.scenarioID === id);
    setSelectedScenario(scenarioObj);
    setSelectedTemplateStr("");
    showSecondDrawer();
  };

  const handleAddScenarioClick = () => {
    const newScenario = defaultScenarioObject();
    setSelectedScenario(newScenario);
    setCurScenariosList([...curScenariosList, newScenario]);
    setSelectedTemplateStr("");
    showSecondDrawer();
  };

  const [scenarioRemoveConfirmation, setScenarioRemoveConfirmation] =
    useState(false);

  const [stepsScenarioOptions, setStepsScenarioOptions] = useState({});
  const [cookiesStepsScenarioOptions, setCookiesStepsScenarioOptions] =
    useState({});
  const [loginStepsScenarioOptions, setLoginStepsScenarioOptions] = useState(
    {},
  );

  const [stepsListChangeModalState, setStepsListChangeModalState] =
    useState(false);
  const [
    cookiesStepsListChangeModalState,
    setCookiesStepsListChangeModalState,
  ] = useState(false);
  const [loginStepsListChangeModalState, setLoginStepsListChangeModalState] =
    useState(false);

  const handleStepsListModalCancel = () =>
    handleStepsListModalChangeState({
      newState: false,
      modalStateChangeFunction: setStepsListChangeModalState,
    });
  const handleCookiesStepsListModalCancel = () =>
    handleStepsListModalChangeState({
      newState: false,
      modalStateChangeFunction: setCookiesStepsListChangeModalState,
    });
  const handleLoginStepsListModalCancel = () =>
    handleStepsListModalChangeState({
      newState: false,
      modalStateChangeFunction: setLoginStepsListChangeModalState,
    });

  const [stepsListChangeModalOkFunction, setStepsListChangeModalOkFunction] =
    useState(() => {});

  const handleStepsListModalChangeState = (props) => {
    const {
      newState = false,
      okFunction = () => {},
      modalStateChangeFunction = () => {},
    } = props;
    modalStateChangeFunction(newState);
    setStepsListChangeModalOkFunction(() => okFunction);
  };

  const getScenariosListProps = (props) => {
    const {
      listUpdateFunction = () => {},
      scenariosDropdownList = [],
      stepsType = "",
      modalCancelFunction = () => {},
      modalStateChangeFunction = () => {},
    } = props;

    return {
      items: scenariosDropdownList,
      onClick: (e) =>
        handleStepsListModalChangeState({
          newState: true,
          okFunction: () =>
            copyStepsListFromAnotherScenario({
              scenarioId: e?.key,
              listUpdateFunction,
              stepsType,
              modalCancelFunction,
            }),
          modalStateChangeFunction,
        }),
    };
  };

  const clearStepsList = (props) => {
    const { listUpdateFunction = () => {}, modalCancelFunction = () => {} } =
      props;
    listUpdateFunction([]);
    modalCancelFunction();
  };

  const copyStepsListFromAnotherScenario = (props) => {
    const {
      scenarioId = "",
      listUpdateFunction = () => {},
      stepsType = "",
      modalCancelFunction = () => {},
    } = props;

    const scenario = curScenariosList?.find(
      (elem) => elem?.scenarioID === scenarioId,
    );
    let steps = [];
    switch (stepsType) {
      case "steps":
        steps = scenario?.steps || [];
        break;
      case "cookies":
        steps = scenario?.cookiesAcceptance?.steps || [];
        break;
      case "login":
        steps = scenario?.loginInfo?.steps || [];
        break;
    }
    listUpdateFunction(steps);
    modalCancelFunction();
  };

  // DataLayer template modal
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateStr, setSelectedTemplateStr] = useState(null);

  const menuProps = {
    items: defaultMenuProps,
    onClick: (e) => addDefaultEcommerceObject(e?.key),
  };

  const clearJSON = () => {
    setSelectedTemplate(null);
    setSelectedTemplateStr(null);
  };

  useEffect(() => {
    if (!initialTemplateRef.current || !thirdDrawerVisible) return;
    const hasChanges = !_.isEqual(initialTemplateRef.current, selectedTemplate);
    setTemplateSaveStatus(hasChanges);
  }, [selectedTemplate, thirdDrawerVisible]);

  const addDefaultEcommerceObject = (eventName) => {
    const obj = defaultEventTemplates[eventName];
    setSelectedTemplate(obj);
    setSelectedTemplateStr(JSON.stringify(obj));
  };

  const [dlEditModalTitle, setDlEditModalTitle] = useState("");
  const [dlEditModalState, setDlEditModalState] = useState(false);
  const [dlEditModalContent, setDlEditModalContent] = useState(false);
  const [editedTextfieldValue, setEditedTextfieldValue] = useState("");
  const defaultEventTemplate = { "": "" };

  useEffect(() => {
    const initialTemplate =
      selectedTemplate?.length === 1 &&
      selectedTemplate[0].key === "" &&
      selectedTemplate[0].value === "";

    const text = initialTemplate ? "add json" : "edit json";
    const icon = initialTemplate ? <PlusOutlined /> : <EditFilled />;
    setJsonBtnText(text);
    setJsonBtnIcon(icon);
  }, [selectedTemplate]);

  const handleEditDlModalOk = () => {
    try {
      const value = jsonrepair(
        editedTextfieldValue || JSON.stringify(defaultEventTemplate),
      );
      saveEditedDlObjectData(value);
    } catch (err) {
      console.log(err);
      error("Incorrect value! Please check the JSON object and try again.");
    }
  };

  const saveEditedDlObjectData = (value) => {
    const parsed = JSON.parse(value);
    const transformedTextFieldValue = transformDataObject(parsed);
    setSelectedTemplate(transformedTextFieldValue);
    setSelectedTemplateStr(JSON.stringify(transformedTextFieldValue));
    handleSaveTemplate(transformedTextFieldValue);
    setDlEditModalState(false);
  };

  const handleEditDlModalCancel = () => setDlEditModalState(false);

  const handleEditTemplateEvent = () => {
    setDlEditModalContent(defaultModalContent());
    setDlEditModalState(true);
  };

  const defaultModalContent = () => {
    const placeholderObj = {
      event: "event_name",
      payload: {
        key: "value",
      },
    };
    const placeholderText = JSON.stringify(placeholderObj, undefined, 2);
    const defaultValueTemplate = simplifyDLGEventJson(selectedTemplate);
    const defaultValueStr = JSON.stringify(defaultValueTemplate, null, 2);
    setEditedTextfieldValue(defaultValueStr);
    setDlEditModalTitle("Template network event payload");

    return (
      <>
        <p>Add your network event payload template in the box below.</p>
        <TextArea
          name="network_event_object_input"
          key={editedTextfieldValue}
          rows={12}
          placeholder={placeholderText}
          defaultValue={defaultValueStr}
          onInput={(e) => setEditedTextfieldValue(e.target.value)}
          style={{
            fontFamily: "monospace",
            fontSize: "14px",
            backgroundColor: "#f7f7f7",
            border: "1px solid #ccc",
            padding: "10px",
            borderRadius: "4px",
            whiteSpace: "pre-wrap",
          }}
        />
      </>
    );
  };

  const handleSaveTemplate = (template = []) => {
    const updatedSteps = scenarioSteps.map((elem) => {
      if (elem.stepId === selectedStepId) {
        elem.dataLayerObjToFind = template;
      }
      return elem;
    });

    const templateSimplified = simplifyDLGEventJson(template);

    const templateStr = JSON.stringify(templateSimplified);
    setEditedTextfieldValue(templateStr);
    setSelectedTemplate(template);
    setSelectedTemplateStr(JSON.stringify(template));
    handleSaveSteps(updatedSteps);
  };

  const handleSaveSteps = (steps = []) => {
    setScenarioSteps(steps);
    const newScenarioObj = selectedScenario;
    newScenarioObj.steps = steps;
    setSelectedScenario(newScenarioObj);
    return handleSaveScenarios(steps);
  };

  const handleSaveScenarios = (stepsToSave = scenarioSteps) => {
    let newScenariosList = [];

    if (selectedScenario) {
      const newScenarioObj = selectedScenario;
      newScenarioObj.scenarioTitle = scenarioTitle;
      newScenarioObj.active = scenarioStatus;
      newScenarioObj.urlStartingPoint = scenarioStartPage;
      newScenarioObj.urlStartingPointMatchType = scenarioStartPageMatchType;
      newScenarioObj.urlStartingPointPageType = scenarioStartPageType;
      newScenarioObj.networkEndpointPattern = networkEndpointPattern;
      newScenarioObj.steps = stepsToSave;

      const newCookiesObj = { ...scenarioCookies };
      newCookiesObj.active = scenarioCookiesStatus;
      newCookiesObj.steps = scenarioCookiesSteps;
      setScenarioCookies(newCookiesObj);

      const newLoginObj = { ...scenarioLogin };
      newLoginObj.required = scenarioLoginStatus;
      newLoginObj.steps = scenarioLoginSteps;
      setScenarioLogin(newLoginObj);

      newScenarioObj.cookiesAcceptance = newCookiesObj;
      newScenarioObj.loginInfo = newLoginObj;

      newScenariosList = curScenariosList.map((elem) =>
        selectedScenario.scenarioID === elem.scenarioID
          ? newScenarioObj
          : elem,
      );
    }

    setCurScenariosList(newScenariosList);
    return newScenariosList;
  };

  const saveScenarioStepsWithGuard = async () => {
    if (isScenarioSaving || !urlTldValid || !scenarioSaveStatus) {
      return;
    }

    setIsScenarioSaving(true);

    try {
      const newScenariosList = handleSaveSteps(scenarioSteps);
      const newConfigParams = {
        ...configParams,
        network_scenarios: newScenariosList,
      };

      setConfigParams(newConfigParams);
      const saveSucceeded = await saveFunction(newConfigParams);

      if (!saveSucceeded) {
        return;
      }

      initialScenarioRef.current = {
        title: scenarioTitle,
        startPage: scenarioStartPage,
        matchType: scenarioStartPageMatchType,
        pageType: scenarioStartPageType,
        steps: _.cloneDeep(scenarioSteps),
      };
      setScenarioSaveStatus(false);
      closeSecondDrawer();
    } finally {
      setIsScenarioSaving(false);
    }
  };

  const handleSaveAction = (level) => {
    switch (level) {
      case "templates":
        handleSaveTemplate(selectedTemplate);
        // Update the scenario baseline so the steps drawer
        // doesn't see the template save as an unsaved change
        if (initialScenarioRef.current) {
          initialScenarioRef.current.steps = [...scenarioSteps];
        }
        closeThirdDrawer();
        break;
      case "steps":
        handleSaveSteps(scenarioSteps);
        closeSecondDrawer();
        break;
      case "cookies":
        handleSaveScenarios();
        closeCookiesDrawer();
        break;
      case "login":
        handleSaveScenarios();
        closeLoginDrawer();
        break;
      case "scenarios":
        handleSaveScenarios();
        setDrawerGlobalState(false);
        break;
    }

    const newConfigParams = { ...configParams };
    newConfigParams.network_scenarios = curScenariosList;
    setConfigParams(newConfigParams);
    setIsConfigParamsUpdatedBeforeSave(true);
  };

  const [isConfigParamsUpdatedBeforeSave, setIsConfigParamsUpdatedBeforeSave] =
    useState(false);

  useEffect(() => {
    if (isConfigParamsUpdatedBeforeSave) {
      saveFunction();
      setIsConfigParamsUpdatedBeforeSave(false);
    }
  }, [isConfigParamsUpdatedBeforeSave]);

  return (
    <div>
      <Drawer
        className="scenarios-drawer"
        placement={defaultPlacement}
        onClose={() => setDrawerGlobalState(false)}
        open={drawerGlobalState}
        closeIcon={null}
        closable={true}
        width={firstDrawerWidth}
        styles={{ header: { display: "none" } }}
      >
        <div className={`modal-elems-rows scenarios-modal-padding`}>
          <div>
            <h2 className="header-letter-spacing">
              Network Inspector scenarios
            </h2>
          </div>
          <div
            style={{ marginBottom: 0 }}
            className="container-guide modal-panel-button-row"
          >
            <Link
              className="modal-guide-banner "
              target="_blank"
              to={networkScenariosDescLink}
              style={{ textDecoration: "none", color: "white" }}
            >
              <div className="panel-button-text">
                <div className="panel-button-desc">
                  Click here to follow the complete setup guide for the
                  Network Inspector.
                  <br /> Question not covered?{" "}
                  <span
                    className="support_request_link"
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open("https://portal.code-cube.io/support_request", "_blank");
                    }}
                  >
                    Please get in contact.
                  </span>
                </div>
              </div>
            </Link>
          </div>
          <div className="modal-text">
            <p>
              This page provides an overview of all scenarios that Network
              Inspector should follow to collect network events. Each scenario
              should replicate the steps taken by a typical user.
            </p>
            <p>
              You can configure multiple scenarios to ensure comprehensive event
              monitoring.
            </p>
          </div>

          <div className="no-sep-header">
            <Table
              size="small"
              columns={scenarioColumns}
              dataSource={curScenariosList}
              rowKey="scenarioID"
              rowClassName="scenarios-list-row"
              onRow={(record) => ({
                onClick: () => handleEditScenarioClick(record?.scenarioID),
              })}
              pagination={false}
            />
          </div>
          <div
            style={{
              marginTop: "32px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-end",
              gap: "18px",
            }}
          >
            <Button type="default" onClick={handleAddScenarioClick}>
              + add scenario
            </Button>
          </div>
          <Modal
            centered
            open={deleteModalVisible}
            title="Delete this scenario?"
            onOk={handleDeleteConfirmed}
            onCancel={() => {
              setDeleteModalVisible(false);
              setTargetScenarioId(null);
            }}
            okButtonProps={{ danger: true }}
          >
            Are you sure you want to delete this scenario?
          </Modal>
          <Modal
            centered
            open={pauseModalVisible}
            title="Pause this scenario?"
            onOk={handlePauseConfirmed}
            onCancel={() => {
              setPauseModalVisible(false);
              setTargetScenarioId(null);
            }}
            okButtonProps={{ danger: true }}
          >
            <p>Are you sure you want to pause this scenario?</p>
          </Modal>
        </div>

        {/* Scenario steps drawer */}
        <Drawer
          className="scenarios-drawer"
          placement={defaultPlacement}
          onClose={() => {
            if (isScenarioSaving) {
              return;
            }
            if (scenarioSaveStatus) {
              setShowUnsavedModal(true);
            } else {
              closeSecondDrawer();
            }
          }}
          open={secondDrawerVisible}
          closeIcon={null}
          closable={true}
          width={secondDrawerWidth}
          styles={{ header: { display: "none" } }}
        >
          <ConfirmationModalWindow
            open={stepsListChangeModalState}
            onOk={stepsListChangeModalOkFunction}
            onCancel={handleStepsListModalCancel}
            text={"Are you sure you want to update the steps list?"}
          />

          {/* Unsaved changes modal */}
          <Modal
            open={showUnsavedModal}
            centered
            title={
              <div className="closeIconDiv">
                <span>Save your changes</span>
                <CloseOutlined
                  onClick={() => setShowUnsavedModal(false)}
                  className="closeIcon"
                />
              </div>
            }
            onCancel={() => setShowUnsavedModal(false)}
            closable={false}
            footer={[
              <Button
                key="discard"
                type="default"
                disabled={isScenarioSaving}
                onClick={() => {
                  if (initialScenarioRef.current) {
                    setScenarioTitle(initialScenarioRef.current.title);
                    setScenarioStartPage(initialScenarioRef.current.startPage);
                    setScenarioStartPageMatchType(
                      initialScenarioRef.current.matchType,
                    );
                    setScenarioStartPageType(
                      initialScenarioRef.current.pageType,
                    );
                    setNetworkEndpointPattern(
                      initialScenarioRef.current.networkEndpointPattern,
                    );
                    setScenarioSteps(initialScenarioRef.current.steps);
                    const isValid = validateDomainTLD(
                      initialScenarioRef.current.startPage,
                    );
                    setUrlTldValid(isValid);
                    setUrlTldError(
                      isValid
                        ? ""
                        : getTLDErrorMessage(
                            initialScenarioRef.current.startPage,
                          ),
                    );
                  }
                  setShowUnsavedModal(false);
                  setScenarioSaveStatus(false);
                  closeSecondDrawer();
                }}
              >
                discard
              </Button>,
              <Button
                key="save"
                type="saveChanges"
                disabled={!urlTldValid || isScenarioSaving}
                onClick={async () => {
                  setShowUnsavedModal(false);
                  await saveScenarioStepsWithGuard();
                }}
              >
                save changes
              </Button>,
            ]}
          >
            <p>Do you want to save changes before leaving this step?</p>
          </Modal>

          <div
            key={isConfigParamsUpdatedBeforeSave}
            className={`modal-elems-rows scenarios-modal-padding`}
          >
            <div>
              <h2 className="header-letter-spacing">
                Set-up details for Network Inspector scenario
              </h2>
            </div>
            <div className="modal-text">
              <p>
                This page provides the details of the Network Inspector
                scenario. Make sure the scenario replicates the steps taken by
                an actual user.
              </p>
            </div>

            <Space size={40} direction="vertical" style={{ width: "100%" }}>
              <Space direction="vertical" size={4}>
                <h3 className="modal-category">Scenario name</h3>
                <div className="modal-text">
                  Give your scenario a descriptive name.
                </div>
                <div style={{ marginTop: 16 }}>
                  <Input
                    name="scenario_title"
                    value={scenarioTitle}
                    style={{ width: "10rem" }}
                    onChange={(e) => setScenarioTitle(e.target.value)}
                  />
                </div>
              </Space>
              <Space direction="vertical" size={4}>
                <h3 className="modal-category">Starting point for scenario</h3>
                <div className="modal-text">
                  Define where the scenario should start.
                </div>
                <div style={{ marginTop: 16 }}>
                  <Space size={5}>
                    <DropdownMenu
                      type="default"
                      text={
                        scenarioStartPageTypes.find(
                          (e) => e.key === scenarioStartPageType,
                        )?.label || "Select page type"
                      }
                      menuProps={menuPageTypes}
                      customStyle={{
                        background: "#fff",
                        color: "rgba(0,0,0,0.85)",
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                        height: 32,
                        padding: "4px 11px",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    />
                    <DropdownMenu
                      type="default"
                      text={
                        scenarioStartPageMatchTypes.find(
                          (e) => e.key === scenarioStartPageMatchType,
                        )?.label || "Select match type"
                      }
                      menuProps={menuMatchTypes}
                      customStyle={{
                        background: "#fff",
                        color: "rgba(0,0,0,0.85)",
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                        height: 32,
                        padding: "4px 11px",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    />
                    <div style={{ position: "relative" }}>
                      <Input
                        name="scenario_start_page"
                        value={scenarioStartPage}
                        placeholder="/tag-monitor"
                        status={!urlTldValid ? "error" : ""}
                        style={{ width: "20rem" }}
                        onChange={handleStartingPointUrlChange}
                      />
                      {!urlTldValid && urlTldError && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            color: "#ff4d4f",
                            fontSize: "12px",
                            marginTop: "4px",
                            lineHeight: "18px",
                          }}
                        >
                          {urlTldError}
                        </div>
                      )}
                    </div>
                  </Space>
                </div>
              </Space>
              <Space direction="vertical" size={4}>
                <h3 className="modal-category">Network endpoint pattern</h3>
                <div className="modal-text">
                  Define which request URLs this scenario should capture.
                </div>
                <div style={{ marginTop: 16 }}>
                  <Input
                    name="network_endpoint_pattern"
                    value={networkEndpointPattern}
                    placeholder={"doubleclick\\.net|googleads"}
                    style={{ width: "30rem" }}
                    onChange={(e) =>
                      setNetworkEndpointPattern(e.target.value)
                    }
                  />
                  <div
                    className="modal-text"
                    style={{ marginTop: 8, maxWidth: "40rem" }}
                  >
                    A regular expression matching the request URLs this
                    scenario should capture — usually the tag/vendor domains
                    to monitor (e.g.
                    doubleclick\.net|googleads|metrics\.example\.com/b/ss/).
                    Without it, the scenario captures every request and
                    checks become unreliable.
                  </div>
                </div>
              </Space>
            </Space>

            {/* Cookies acceptance & Login required side by side */}
            <Flex gap={60} style={{ marginTop: 40 }}>
              <Space direction="vertical" size={4}>
                <h3 className="modal-category">Cookies acceptance</h3>
                <Space size={25}>
                  <Space>
                    <Switch
                      value={scenarioCookiesStatus}
                      onChange={(value) => setScenarioCookiesStatus(value)}
                    />
                    <label>
                      {scenarioCookiesStatus ? "Required" : "Not required"}
                    </label>
                  </Space>
                  {scenarioCookiesStatus && (
                    <Button type="default" onClick={showCookiesDrawer}>
                      Edit cookies steps
                    </Button>
                  )}
                </Space>
              </Space>

              <Space direction="vertical" size={4}>
                <h3 className="modal-category">Login required</h3>
                <Space size={25}>
                  <Space>
                    <Switch
                      value={scenarioLoginStatus}
                      onChange={(value) => setScenarioLoginStatus(value)}
                    />
                    <label>
                      {scenarioLoginStatus ? "Required" : "Not required"}
                    </label>
                  </Space>
                  {scenarioLoginStatus && (
                    <Button type="default" onClick={showLoginDrawer}>
                      Edit login steps
                    </Button>
                  )}
                </Space>
              </Space>
            </Flex>

            {/* Steps list */}
            <Space direction="vertical" size={4} style={{ marginTop: 40 }}>
              <h3 className="modal-category">Steps to take</h3>
              <div className="modal-text">
                Define the steps the scenario should follow.
              </div>
              <div style={{ marginTop: 16 }}>
                <NetworkSortableList
                  items={scenarioSteps}
                  handleDataLayerObjEdit={showThirdDrawer}
                  handleStepsUpdate={setScenarioSteps}
                />
              </div>
            </Space>
          </div>

          {/* Save button */}
          <div className="drawer-buttons-area">
            <div className="drawer-buttons-area-shad"></div>
            <div className="drawer-buttons-area-button">
              <ActionButton
                text={isScenarioSaving ? "Saving" : "Save"}
                size="large"
                type="save"
                width={140}
                loading={isScenarioSaving}
                disabled={isScenarioSaving || !urlTldValid || !scenarioSaveStatus}
                onClickFunction={saveScenarioStepsWithGuard}
              >
                Save
              </ActionButton>
            </div>
          </div>

          {/* Delete scenario confirmation */}
          <Modal
            centered
            open={scenarioRemoveConfirmation}
            title="Confirm"
            onOk={() => scenarioRemove(selectedScenario?.scenarioID)}
            onCancel={() => setScenarioRemoveConfirmation(false)}
            footer={(_, { OkBtn, CancelBtn }) => (
              <Flex gap={5}>
                <CancelBtn />
                <OkBtn />
              </Flex>
            )}
          >
            <p>Do you want to delete this scenario?</p>
          </Modal>

          {/* Template editor drawer */}
          <Drawer
            classNames={
              thirdDrawerClassName
                ? { wrapper: thirdDrawerClassName }
                : undefined
            }
            title={thirdDrawerTitle}
            placement={defaultPlacement}
            onClose={() => {
              if (templateSaveStatus) {
                setShowTemplateUnsavedModal(true);
              } else {
                closeThirdDrawer();
              }
            }}
            open={thirdDrawerVisible}
            closable={true}
            width={thirdDrawerWidth}
            styles={{ header: { display: "none" } }}
          >
            <Modal
              centered
              title={dlEditModalTitle}
              open={dlEditModalState}
              onOk={handleEditDlModalOk}
              onCancel={handleEditDlModalCancel}
              footer={(_, { OkBtn, CancelBtn }) => (
                <Flex gap={5}>
                  <CancelBtn />
                  <OkBtn />
                </Flex>
              )}
            >
              {dlEditModalContent}
            </Modal>

            {/* Unsaved template changes modal */}
            <Modal
              open={showTemplateUnsavedModal}
              centered
              title={
                <div className="closeIconDiv">
                  <span>Save your changes</span>
                  <CloseOutlined
                    onClick={() => setShowTemplateUnsavedModal(false)}
                    className="closeIcon"
                  />
                </div>
              }
              onCancel={() => setShowTemplateUnsavedModal(false)}
              closable={false}
              footer={[
                <Button
                  key="discard"
                  type="default"
                  onClick={() => {
                    setSelectedTemplate(initialTemplateRef.current);
                    setSelectedTemplateStr(
                      JSON.stringify(initialTemplateRef.current),
                    );
                    setShowTemplateUnsavedModal(false);
                    setTemplateSaveStatus(false);
                    closeThirdDrawer();
                  }}
                >
                  discard
                </Button>,
                <Button
                  key="save"
                  type="saveChanges"
                  onClick={() => {
                    handleSaveAction("templates");
                    setShowTemplateUnsavedModal(false);
                    setTemplateSaveStatus(false);
                  }}
                >
                  save changes
                </Button>,
              ]}
            >
              <p>Do you want to save changes before leaving?</p>
            </Modal>

            <div
              key="template_main"
              className="modal-elems-rows scenarios-modal-padding"
            >
              <div>
                <h2 className="header-submodal header-letter-spacing">
                  Network event payload template
                </h2>
              </div>
              <div>
                <h3 className="modal-category fw-bold">Add event details</h3>
                <p className="mb-0">
                  Define the expected structure of the network event payload.
                  <br />
                  For a quick start you can paste your JSON object via the
                  button below.
                </p>
              </div>
              <Flex gap={10}>
                <ActionButton
                  text={jsonBtnText}
                  type=""
                  icon={jsonBtnIcon}
                  onClickFunction={() => handleEditTemplateEvent()}
                  width={120}
                />
                <ActionButton
                  text="clean json"
                  type=""
                  icon={<RedoOutlined />}
                  onClickFunction={clearJSON}
                />
              </Flex>
              <Space>
                <NestedObjectTable
                  key={selectedStepId}
                  data={selectedTemplateStr}
                  updateTemplateFunction={setSelectedTemplate}
                  setEditedEventTemplateNewVersion={setSelectedTemplate}
                />
              </Space>
            </div>

            <div className="drawer-buttons-area">
              <div className="drawer-buttons-area-shad"></div>
              <div className="drawer-buttons-area-button">
                <ActionButton
                  size="large"
                  type="save"
                  width={140}
                  onClickFunction={() => {
                    handleSaveAction("templates");
                    setTemplateSaveStatus(false);
                  }}
                >
                  Save
                </ActionButton>
              </div>
            </div>
          </Drawer>

          {/* Cookies drawer */}
          <Drawer
            title={cookiesDrawerTitle}
            placement={defaultPlacement}
            onClose={closeCookiesDrawer}
            open={cookiesDrawerVisible}
            closable={true}
            width={thirdDrawerWidth}
            styles={{ header: { display: "none" } }}
          >
            <ConfirmationModalWindow
              open={cookiesStepsListChangeModalState}
              onOk={stepsListChangeModalOkFunction}
              onCancel={handleCookiesStepsListModalCancel}
              text={"Are you sure you want to update the steps list?"}
            />
            <div
              key={isConfigParamsUpdatedBeforeSave}
              className={`modal-elems-rows scenarios-modal-padding`}
            >
              <div>
                <h2 className="header-letter-spacing">
                  Cookies Acceptance Scenario Steps
                </h2>
              </div>
              <Space size={25}>
                <Space>
                  <DropdownMenu
                    text="Import steps"
                    menuProps={cookiesStepsScenarioOptions}
                    type="default"
                    loading={false}
                    icon={<CopyOutlined />}
                    iconPosition="start"
                    customStyle={{
                      background: "#fff",
                      color: "rgba(0,0,0,0.85)",
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      height: 32,
                      padding: "4px 11px",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  />
                  <ActionButton
                    text={
                      <Space>
                        <ClearOutlined />
                        Clear steps
                      </Space>
                    }
                    onClickFunction={() =>
                      handleStepsListModalChangeState({
                        newState: true,
                        okFunction: () =>
                          clearStepsList({
                            listUpdateFunction: setScenarioCookiesSteps,
                            modalCancelFunction:
                              handleCookiesStepsListModalCancel,
                          }),
                        modalStateChangeFunction:
                          setCookiesStepsListChangeModalState,
                      })
                    }
                  />
                </Space>
              </Space>
              <div style={{ paddingBottom: "5rem" }}>
                <h3 className="modal-category">Steps</h3>
                <div style={{ margin: "auto" }}>
                  <NetworkSortableList
                    items={scenarioCookiesSteps}
                    handleDataLayerObjEdit={() => {}}
                    handleStepsUpdate={setScenarioCookiesSteps}
                  />
                </div>
              </div>
            </div>
            <div className="drawer-buttons-area">
              <div className="drawer-buttons-area-shad"></div>
              <div className="drawer-buttons-area-button">
                <ActionButton
                  size="large"
                  type="save"
                  width={140}
                  onClickFunction={() => handleSaveAction("cookies")}
                >
                  Save
                </ActionButton>
              </div>
            </div>
          </Drawer>

          {/* Login drawer */}
          <Drawer
            className="scenarios-drawer"
            placement={defaultPlacement}
            onClose={closeLoginDrawer}
            open={loginDrawerVisible}
            closeIcon={null}
            closable={true}
            width={firstDrawerWidth}
            styles={{ header: { display: "none" } }}
          >
            <ConfirmationModalWindow
              open={loginStepsListChangeModalState}
              onOk={stepsListChangeModalOkFunction}
              onCancel={handleLoginStepsListModalCancel}
              text={"Are you sure you want to update the steps list?"}
            />
            <div
              key={isConfigParamsUpdatedBeforeSave}
              className={`modal-elems-rows scenarios-modal-padding`}
            >
              <div>
                <h2 className="header-letter-spacing">
                  Set-up details for login scenario
                </h2>
              </div>
              <div className="modal-text">
                <p>
                  This page provides an overview of login scenario that
                  Network Inspector should follow to successfully login.
                </p>
              </div>
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <h3 className="modal-category">
                  Steps to take for successful login
                </h3>
                <div style={{ width: "100%" }}>
                  <NetworkSortableList
                    items={scenarioLoginSteps}
                    handleDataLayerObjEdit={() => {}}
                    handleStepsUpdate={setScenarioLoginSteps}
                  />
                </div>
              </Space>
            </div>
            <div className="drawer-buttons-area">
              <div className="drawer-buttons-area-shad"></div>
              <div className="drawer-buttons-area-button">
                <ActionButton
                  size="large"
                  type="save"
                  width={140}
                  onClickFunction={() => handleSaveAction("login")}
                >
                  Save
                </ActionButton>
              </div>
            </div>
          </Drawer>
        </Drawer>
      </Drawer>
    </div>
  );
};

export default NetworkScenariosMenu;
